import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { explainPaperStyle, explainExampleTitleStyle, explainPaperTitleStyle, explainPaperTextStyle, explainExamplePaperStyle, explainExampleTextStyle, explainExampleImageBoxStyle} from '../../../../styles/commonStyles';

// 연수 메타데이터
export interface TrainingMetadata {
  id: string;
  title: string;
  category: string;
  description: string;
  instructor: string;
  duration: string;
  date: string;
  tags: string[];
  materials: string[];
  status: 'active' | 'completed' | 'upcoming';
  path: string;
}

export const trainingMetadata: TrainingMetadata = {
  id: 'sunrin-ai-training',
  title: '선린중학교 AI 연수',
  category: 'ai-edutech',
  description: 'Gemini Pro와 Canva AI를 활용한 교육 현장 AI 도구 활용 연수입니다.',
  instructor: '한지호',
  duration: '2시간',
  date: '2025-01-01',
  tags: ['AI', 'Gemini', 'Canva', '교육혁신'],
  materials: ['SunrinAITraining.tsx'],
  status: 'active',
  path: '/teacher-training/AIEdutech/SunrinAITraining'
};

const SunrinAITraining: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div style={{ 
      width: '100%', 
      minHeight: '100%', 
      boxSizing: 'border-box', 
      padding: '24px', 
      margin: 0, 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'auto'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/teacher-training/AIEdutech')}
          style={{
            marginRight: '16px',
            padding: '8px 16px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ← 돌아가기
        </button>
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>
          선린중학교 AI 연수
        </h1>
      </div>

      {/* 목차 */}
      <div style={{ 
        marginBottom: '24px', 
        border: '1px solid #ccc', 
        padding: '16px', 
        borderRadius: '8px', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        backgroundColor: 'white'
      }}>
        <h2 style={{ fontWeight: 'bold', marginBottom: '16px', fontSize: '1.25rem' }}>
          📚 목차
        </h2>
        <ul style={{ listStyleType: 'none', padding: 0 }}>
          <li style={{marginBottom: '8px'}}>
            <a href="#연수-개요" style={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
              연수 개요
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
            <a href="#gemini-pro-소개" style={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
              Gemini Pro 소개
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
            <a href="#gemini-pro-활용" style={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
              Gemini Pro 활용하기
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
            <a href="#canva-ai-소개" style={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
              Canva AI 소개
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
            <a href="#canva-ai-활용" style={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
              Canva AI 활용하기
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
            <a href="#실습-과제" style={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
              실습 과제
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
            <a href="#참고-자료" style={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
              참고 자료
            </a>
          </li>
        </ul>
      </div>

      {/* 연수 소개 */}
      <div style={{ display: 'flex', alignItems: 'end', marginBottom: '24px', gap: '16px' }}>
        <p style={{ color: '#666', margin: 0, fontSize: '1rem', lineHeight: '1.6' }}>
          선린중학교 선생님들과 함께하는 AI 연수 페이지입니다.<br/>
          Gemini Pro와 Canva AI를 활용하여 교육 현장에서 바로 사용할 수 있는 실전 활용법을 다룹니다.<br/>
        </p>
      </div>

      {/* 연수 개요 */}
      <h2 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.5rem' }} id="연수-개요">
        연수 개요
      </h2>
      <div style={{
        backgroundColor: '#f8f9fa',
        border: '2px solid #667eea',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <p style={{ 
          color: '#333', 
          margin: 0, 
          fontSize: '1.125rem', 
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          📅 연수 일정: 2025년 12월 29일 (월) 15:30 ~ 16:30 (1시간)
        </p>
      </div>
      <p style={{ color: '#666', marginBottom: '8px', fontSize: '1rem', lineHeight: '1.6' }}>
        본 연수는 다음과 같은 내용으로 구성됩니다.
      </p>
      <ul style={{ color: '#666', marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
        <li>
          <b>Gemini Pro 이해하기</b>: Google의 최신 AI 모델인 Gemini Pro의 특징과 요금제 차이
        </li>
        <li>
          <b>Gemini Pro + Workspace 활용</b>: 이메일, 캘린더, 드라이브 등과 연동한 업무 효율화
        </li>
        <li>
          <b>Gemini Pro + Nanobanana Pro</b>: 이미지 편집 및 생성 기능 활용
        </li>
        <li>
          <b>Gemini Pro + Canvas</b>: 수업 자료 및 콘텐츠 제작
        </li>
        <li>
          <b>Canva AI 활용</b>: PPT 제작 및 웹페이지 생성
        </li>
      </ul>

      {/* Gemini Pro 소개 */}
      <h2 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.5rem' }} id="gemini-pro-소개">
        ✨ Gemini Pro 소개
      </h2>
      <p style={{ color: '#666', marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
        Gemini Pro는 Google에서 개발한 최신 대규모 언어 모델(LLM)입니다. 텍스트, 이미지, 비디오를 동시에 이해하고 처리할 수 있는 멀티모달 AI로, 다양한 교육 활동에 활용할 수 있습니다.
      </p>

      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600', 
          margin: 0,
          marginBottom: '16px',
          color: '#4285F4'
        }}>
          주요 특징
        </h3>
        <ul style={{ 
          fontSize: '1rem', 
          color: '#666', 
          margin: 0,
          paddingLeft: '20px',
          lineHeight: '1.8'
        }}>
          <li><b>멀티모달 이해</b>: 텍스트, 이미지, 비디오를 동시에 분석하고 처리</li>
          <li><b>Google Workspace 연동</b>: Gmail, Google Drive, Calendar 등과 완벽하게 통합</li>
          <li><b>실시간 대화</b>: 자연스러운 대화 형식으로 반복적 개선 가능</li>
          <li><b>확장 기능 지원</b>: Nanobanana Pro, Canvas 등 다양한 확장 기능 활용</li>
          <li><b>고품질 응답</b>: 교육 자료 제작, 문제 생성, 콘텐츠 개발 등 전문적 지원</li>
        </ul>
      </div>

      {/* 요금제 차이 */}
      <div style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <h3 style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600', 
          margin: 0,
          marginBottom: '16px',
          color: '#333'
        }}>
          💰 Google One 요금제
        </h3>
        <p style={{ 
          fontSize: '0.875rem', 
          color: '#666', 
          margin: 0,
          marginBottom: '16px',
          lineHeight: '1.6'
        }}>
          Google One 구독을 통해 Gemini AI 기능을 사용할 수 있습니다. 자세한 요금제 정보는 <a href="https://one.google.com/intl/ko_kr/about/#compare-plans" target="_blank" rel="noopener" style={{color: '#1976d2', textDecoration: 'underline'}}>Google One 요금제 페이지</a>를 확인해주세요.
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '16px',
          marginBottom: '16px'
        }}>
          {/* 무료 플랜 */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '16px',
            border: '2px solid #e0e0e0'
          }}>
            <h4 style={{ 
              fontSize: '1.125rem', 
              fontWeight: '600', 
              margin: 0,
              marginBottom: '12px',
              color: '#4285F4'
            }}>
              무료 플랜
            </h4>
            <p style={{ 
              fontSize: '0.75rem', 
              color: '#666', 
              margin: 0,
              marginBottom: '12px',
              fontWeight: '500'
            }}>
              기본 제공: 15GB 스토리지
            </p>
            <ul style={{ 
              fontSize: '0.875rem', 
              color: '#666', 
              margin: 0,
              paddingLeft: '20px',
              lineHeight: '1.6'
            }}>
              <li>기본 Gemini 모델 사용</li>
              <li>일일 사용량 제한</li>
              <li>제한된 AI 기능</li>
              <li>기본 Workspace 연동</li>
            </ul>
          </div>

          {/* Google AI Pro */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '16px',
            border: '2px solid #4285F4'
          }}>
            <h4 style={{ 
              fontSize: '1.125rem', 
              fontWeight: '600', 
              margin: 0,
              marginBottom: '12px',
              color: '#4285F4'
            }}>
              Google AI Pro
            </h4>
            <p style={{ 
              fontSize: '0.75rem', 
              color: '#666', 
              margin: 0,
              marginBottom: '12px',
              fontWeight: '500'
            }}>
              2TB 스토리지 + Gemini 앱
            </p>
            <ul style={{ 
              fontSize: '0.875rem', 
              color: '#666', 
              margin: 0,
              paddingLeft: '20px',
              lineHeight: '1.6'
            }}>
              <li>Gemini 앱 프리미엄 기능</li>
              <li>Gmail, Docs 등에서 Gemini 활용</li>
              <li>확장된 AI 기능 및 사용량</li>
              <li>Workspace 완전 연동</li>
              <li>2TB 클라우드 스토리지</li>
              <li>최대 5명과 스토리지 공유</li>
            </ul>
          </div>

          {/* Google AI Ultra */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '16px',
            border: '2px solid #8B5CF6'
          }}>
            <h4 style={{ 
              fontSize: '1.125rem', 
              fontWeight: '600', 
              margin: 0,
              marginBottom: '12px',
              color: '#8B5CF6'
            }}>
              Google AI Ultra
            </h4>
            <p style={{ 
              fontSize: '0.75rem', 
              color: '#666', 
              margin: 0,
              marginBottom: '12px',
              fontWeight: '500'
            }}>
              30TB 스토리지 + Gemini 앱
            </p>
            <ul style={{ 
              fontSize: '0.875rem', 
              color: '#666', 
              margin: 0,
              paddingLeft: '20px',
              lineHeight: '1.6'
            }}>
              <li>고급 AI 기능 및 모델</li>
              <li>더 높은 사용량 한도</li>
              <li>모든 Gemini 프리미엄 기능</li>
              <li>30TB 클라우드 스토리지</li>
              <li>Workspace 프리미엄 기능</li>
              <li>YouTube Premium 포함 (일부 국가)</li>
            </ul>
          </div>
        </div>
        <div style={{
          backgroundColor: '#e8f0fe',
          borderRadius: '8px',
          padding: '12px',
          borderLeft: '4px solid #4285F4',
          marginTop: '16px'
        }}>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#333', 
            margin: 0,
            lineHeight: '1.6'
          }}>
            💡 <b>참고사항</b><br/>
            • Google One 요금제는 Google Drive, Gmail, Google 포토에서 공유 스토리지를 제공합니다.<br/>
            • Google AI Pro와 Ultra는 Gemini 앱의 프리미엄 기능에 대한 확장된 액세스를 제공합니다.<br/>
            • 교육 기관용 할인 혜택이 있을 수 있습니다. 자세한 내용은 <a href="https://edu.google.com" target="_blank" rel="noopener" style={{color: '#1976d2', textDecoration: 'underline'}}>Google for Education</a> 페이지를 확인해주세요.
          </p>
        </div>
      </div>

      {/* Gemini Pro 활용하기 */}
      <h2 style={{ fontWeight: 'bold', marginBottom: '16px', fontSize: '1.5rem' }} id="gemini-pro-활용">
        🚀 Gemini Pro 활용하기
      </h2>

      {/* 1. Gemini Pro + Workspace */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600', 
          margin: 0,
          marginBottom: '12px',
          color: '#4285F4'
        }}>
          1. Gemini Pro + Workspace 연결하기
        </h3>
        <p style={{ 
          fontSize: '1rem', 
          color: '#666', 
          margin: 0,
          marginBottom: '16px',
          lineHeight: '1.6'
        }}>
          Gemini Pro를 Google Workspace와 연결하면 이메일, 캘린더, 투두리스트, 드라이브 등과 연동하여 더욱 강력한 AI 어시스턴트로 활용할 수 있습니다.
        </p>
        <div style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <h4 style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            margin: 0,
            marginBottom: '12px',
            color: '#333'
          }}>
            주요 연동 기능
          </h4>
          <ul style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.8'
          }}>
            <li><b>Gmail 연동</b>: 이메일 작성, 요약, 답변 작성 지원</li>
            <li><b>Google Calendar</b>: 일정 관리, 회의 초대, 일정 제안</li>
            <li><b>Google Tasks</b>: 할 일 목록 관리 및 우선순위 설정</li>
            <li><b>Google Drive</b>: 문서 검색, 파일 요약, 내용 분석</li>
            <li><b>Google Docs/Sheets/Slides</b>: 문서 작성 및 편집 지원</li>
          </ul>
        </div>
        <div style={{
          backgroundColor: '#e8f0fe',
          borderRadius: '8px',
          padding: '16px',
          borderLeft: '4px solid #4285F4'
        }}>
          <h4 style={{ 
            fontSize: '0.875rem', 
            fontWeight: '600', 
            margin: 0,
            marginBottom: '8px',
            color: '#4285F4'
          }}>
            💡 활용 예시
          </h4>
          <ul style={{ 
            fontSize: '0.875rem', 
            color: '#333', 
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.6'
          }}>
            <li>"오늘 받은 이메일 중 중요한 것만 요약해줘"</li>
            <li>"다음 주 월요일 오후 2시에 회의 일정 잡아줘"</li>
            <li>"드라이브에서 수학 시험 문제 파일 찾아서 난이도 분석해줘"</li>
            <li>"내일 할 일 목록 만들고 우선순위 정해줘"</li>
          </ul>
        </div>
      </div>

      {/* 2. Gemini Pro + Nanobanana Pro */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600', 
          margin: 0,
          marginBottom: '12px',
          color: '#4285F4'
        }}>
          2. Gemini Pro + Nanobanana Pro 사용하기
        </h3>
        <p style={{ 
          fontSize: '1rem', 
          color: '#666', 
          margin: 0,
          marginBottom: '16px',
          lineHeight: '1.6'
        }}>
          Nanobanana Pro는 Gemini Pro의 확장 기능으로, 이미지 편집 및 생성 기능을 제공합니다. 사진을 업로드하여 다양한 스타일로 변경하거나 편집할 수 있습니다.
        </p>
        
        <div style={{
          backgroundColor: '#fff3e0',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          borderLeft: '4px solid #ff9800'
        }}>
          <h4 style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            margin: 0,
            marginBottom: '12px',
            color: '#333'
          }}>
            예시 1: 내 사진으로 헤어스타일 그리드 만들기
          </h4>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            marginBottom: '12px',
            lineHeight: '1.6'
          }}>
            자신의 사진을 업로드하고 "이 사진으로 다양한 헤어스타일 그리드를 만들어줘"라고 요청하면, 
            여러 가지 헤어스타일 옵션을 포함한 그리드를 생성해줍니다.
          </p>
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            marginTop: '12px'
          }}>
            <div style={{
              flex: '1',
              minWidth: '200px',
              maxWidth: '300px'
            }}>
              <p style={{ fontSize: '0.75rem', color: '#666', margin: '0 0 8px 0', fontWeight: '500' }}>원본 사진</p>
              <img 
                src={encodeURI("/images/notes/원본(내사진).jpeg")} 
                alt="원본 사진" 
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              />
            </div>
            <div style={{
              flex: '1',
              minWidth: '200px',
              maxWidth: '300px'
            }}>
              <p style={{ fontSize: '0.75rem', color: '#666', margin: '0 0 8px 0', fontWeight: '500' }}>헤어스타일 그리드 결과</p>
              <img 
                src={encodeURI("/images/notes/헤어스타일추천.png")} 
                alt="헤어스타일 그리드" 
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              />
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: '#fff3e0',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          borderLeft: '4px solid #ff9800'
        }}>
          <h4 style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            margin: 0,
            marginBottom: '12px',
            color: '#333'
          }}>
            예시 2: 정장 착용 증명사진으로 변경하기
          </h4>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            marginBottom: '12px',
            lineHeight: '1.6'
          }}>
            캐주얼한 사진을 업로드하고 "이 사진을 정장을 입은 증명사진 스타일로 변경해줘"라고 요청하면, 
            AI가 자연스럽게 정장을 입은 모습으로 변환해줍니다.
          </p>
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            marginTop: '12px'
          }}>
            <div style={{
              flex: '1',
              minWidth: '200px',
              maxWidth: '300px'
            }}>
              <p style={{ fontSize: '0.75rem', color: '#666', margin: '0 0 8px 0', fontWeight: '500' }}>원본 사진</p>
              <img 
                src={encodeURI("/images/notes/원본(내사진).jpeg")} 
                alt="원본 사진" 
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              />
            </div>
            <div style={{
              flex: '1',
              minWidth: '200px',
              maxWidth: '300px'
            }}>
              <p style={{ fontSize: '0.75rem', color: '#666', margin: '0 0 8px 0', fontWeight: '500' }}>정장 착용 결과</p>
              <img 
                src={encodeURI("/images/notes/정장.png")} 
                alt="정장 착용 증명사진" 
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              />
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: '#fff3e0',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          borderLeft: '4px solid #ff9800'
        }}>
          <h4 style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            margin: 0,
            marginBottom: '12px',
            color: '#333'
          }}>
            예시 3: 교무실 출입금지 표지판 만들기
          </h4>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            marginBottom: '12px',
            lineHeight: '1.6'
          }}>
            교무실 출입금지 포스터를 만들때에도 적합한 사진을 고른 뒤에 문구나 일부 사진을 바꿔달라고 요청해보세요.
          </p>
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            marginTop: '12px'
          }}>
            <div style={{
              flex: '1',
              minWidth: '200px',
              maxWidth: '300px'
            }}>
              <p style={{ fontSize: '0.75rem', color: '#666', margin: '0 0 8px 0', fontWeight: '500' }}>원본 이미지 1: 영화 포스터</p>
              <img 
                src={encodeURI("/images/notes/영화포스터.png")} 
                alt="영화 포스터 원본" 
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              />
            </div>
            <div style={{
              flex: '1',
              minWidth: '200px',
              maxWidth: '300px'
            }}>
              <p style={{ fontSize: '0.75rem', color: '#666', margin: '0 0 8px 0', fontWeight: '500' }}>원본 이미지 2: 내 사진</p>
              <img 
                src={encodeURI("/images/notes/원본(내사진).jpeg")} 
                alt="원본 사진" 
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              />
            </div>
          </div>
          <div style={{
            marginTop: '16px',
            maxWidth: '500px'
          }}>
            <p style={{ fontSize: '0.75rem', color: '#666', margin: '0 0 8px 0', fontWeight: '500' }}>교무실 출입금지 포스터 결과</p>
            <img 
              src={encodeURI("/images/notes/교무실 출입금지.png")} 
              alt="교무실 출입금지 포스터" 
              style={{
                width: '100%',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            />
          </div>
        </div>

        <div style={{
          backgroundColor: '#e8f0fe',
          borderRadius: '8px',
          padding: '16px',
          borderLeft: '4px solid #4285F4'
        }}>
          <h4 style={{ 
            fontSize: '0.875rem', 
            fontWeight: '600', 
            margin: 0,
            marginBottom: '8px',
            color: '#4285F4'
          }}>
            💡 활용 팁
          </h4>
          <ul style={{ 
            fontSize: '0.875rem', 
            color: '#333', 
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.6'
          }}>
            <li>Nanobanana Pro 확장 기능을 Gemini에서 활성화해야 사용 가능</li>
            <li>이미지 품질이 좋을수록 더 나은 결과를 얻을 수 있음</li>
            <li>구체적인 요청을 할수록 원하는 결과에 가까워짐</li>
            <li>여러 번의 대화를 통해 결과를 점진적으로 개선 가능</li>
          </ul>
        </div>
      </div>

      {/* 3. Gemini Pro + Canvas (교과서 파일로 구글 슬라이드) */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600', 
          margin: 0,
          marginBottom: '12px',
          color: '#4285F4'
        }}>
          3. Gemini Pro + Canvas (교과서 파일로 구글 슬라이드 만들기)
        </h3>
        <p style={{ 
          fontSize: '1rem', 
          color: '#666', 
          margin: 0,
          marginBottom: '16px',
          lineHeight: '1.6'
        }}>
          Canvas 확장 기능을 사용하면 Gemini Pro가 교과서 PDF 파일을 읽고 분석하여 Google Slides로 수업 자료를 자동으로 만들어줍니다.
        </p>
        <div style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <h4 style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            margin: 0,
            marginBottom: '12px',
            color: '#333'
          }}>
            사용 방법
          </h4>
          <ol style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.8'
          }}>
            <li>Canvas 확장 기능을 Gemini Pro에서 활성화</li>
            <li>교과서 PDF 파일을 Google Drive에 업로드</li>
            <li>Gemini에게 "이 PDF 파일의 내용을 기반으로 Google Slides 수업 자료를 만들어줘"라고 요청</li>
            <li>원하는 단원이나 페이지 범위를 지정하면 해당 부분만 추출 가능</li>
            <li>생성된 슬라이드를 확인하고 Gemini와 대화하며 수정 및 개선</li>
          </ol>
        </div>
        <div style={{
          backgroundColor: '#e8f0fe',
          borderRadius: '8px',
          padding: '16px',
          borderLeft: '4px solid #4285F4'
        }}>
          <h4 style={{ 
            fontSize: '0.875rem', 
            fontWeight: '600', 
            margin: 0,
            marginBottom: '8px',
            color: '#4285F4'
          }}>
            💡 활용 예시
          </h4>
          <ul style={{ 
            fontSize: '0.875rem', 
            color: '#333', 
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.6'
          }}>
            <li>"수학 교과서 3단원을 기반으로 학생들이 이해하기 쉽게 슬라이드를 만들어줘"</li>
            <li>"과학 교과서 5장을 읽고 핵심 개념만 정리한 발표 자료 만들어줘"</li>
            <li>"국어 교과서 소설 부분을 읽고 분석 문제와 함께 수업 자료 만들어줘"</li>
          </ul>
        </div>
      </div>

      {/* 4. Gemini Pro + Canvas (바이브코딩, 수업 콘텐츠) */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600', 
          margin: 0,
          marginBottom: '12px',
          color: '#4285F4'
        }}>
          4. Gemini Pro + Canvas (간단한 바이브코딩, 수업 콘텐츠 만들기)
        </h3>
        <p style={{ 
          fontSize: '1rem', 
          color: '#666', 
          margin: 0,
          marginBottom: '16px',
          lineHeight: '1.6'
        }}>
          Canvas와 함께 사용하면 Gemini Pro가 자연어 설명만으로도 HTML, CSS, JavaScript 코드를 생성하여 인터랙티브한 수업 콘텐츠를 만들 수 있습니다.
        </p>
        <div style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <h4 style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            margin: 0,
            marginBottom: '12px',
            color: '#333'
          }}>
            바이브코딩의 개념
          </h4>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            lineHeight: '1.6'
          }}>
            바이브코딩(Vibe Coding)은 복잡한 코드 작성 없이 자연어로 AI와 대화하며 원하는 결과물을 만들어내는 방식입니다. 
            Gemini Pro + Canvas 조합으로 HTML/CSS/JavaScript 코드를 자동 생성하고, 실시간으로 미리보기를 확인하며 반복적으로 개선할 수 있습니다.
          </p>
        </div>
        <div style={{
          backgroundColor: '#e8f0fe',
          borderRadius: '8px',
          padding: '16px',
          borderLeft: '4px solid #4285F4'
        }}>
          <h4 style={{ 
            fontSize: '0.875rem', 
            fontWeight: '600', 
            margin: 0,
            marginBottom: '8px',
            color: '#4285F4'
          }}>
            💡 활용 예시
          </h4>
          <ul style={{ 
            fontSize: '0.875rem', 
            color: '#333', 
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.6'
          }}>
            <li>"학생들이 클릭하면 정답을 확인할 수 있는 퀴즈 웹페이지 만들어줘"</li>
            <li>"타이머가 있는 온라인 시험 페이지 만들어줘. 시간이 끝나면 자동으로 제출되도록 해줘"</li>
            <li>"학생 이름을 입력하면 랜덤으로 조를 나누어주는 웹 도구 만들어줘"</li>
            <li>"과학 실험 결과를 시각화하는 인터랙티브 차트 페이지 만들어줘"</li>
          </ul>
        </div>
      </div>

      {/* 5. Google Flow (영상 제작) */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600', 
          margin: 0,
          marginBottom: '12px',
          color: '#4285F4'
        }}>
          5. Google Flow (영상 제작)
        </h3>
        <p style={{ 
          fontSize: '1rem', 
          color: '#666', 
          margin: 0,
          marginBottom: '16px',
          lineHeight: '1.6'
        }}>
          Google Flow는 Gemini Pro와 연동하여 텍스트나 이미지를 기반으로 자동으로 영상을 생성하는 도구입니다. 수업 소개 영상, 학습 콘텐츠 영상 등을 빠르게 제작할 수 있습니다.
        </p>
        <div style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <h4 style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            margin: 0,
            marginBottom: '12px',
            color: '#333'
          }}>
            주요 기능
          </h4>
          <ul style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.8'
          }}>
            <li><b>자동 영상 생성</b>: 텍스트 스크립트나 이미지를 기반으로 영상 자동 제작</li>
            <li><b>음성 내레이션</b>: AI가 자동으로 음성을 생성하여 영상에 삽입</li>
            <li><b>자막 생성</b>: 영상에 자막을 자동으로 추가</li>
            <li><b>템플릿 활용</b>: 다양한 교육용 영상 템플릿 제공</li>
            <li><b>수정 및 편집</b>: 생성된 영상을 실시간으로 수정하고 개선</li>
          </ul>
        </div>
        <div style={{
          backgroundColor: '#e8f0fe',
          borderRadius: '8px',
          padding: '16px',
          borderLeft: '4px solid #4285F4'
        }}>
          <h4 style={{ 
            fontSize: '0.875rem', 
            fontWeight: '600', 
            margin: 0,
            marginBottom: '8px',
            color: '#4285F4'
          }}>
            💡 활용 예시
          </h4>
          <ul style={{ 
            fontSize: '0.875rem', 
            color: '#333', 
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.6'
          }}>
            <li>"이번 주 수업 소개 영상 만들어줘. 1분 분량으로 해줘"</li>
            <li>"과학 실험 과정을 설명하는 교육 영상 만들어줘"</li>
            <li>"학생 작품 발표 영상을 템플릿에 맞춰서 만들어줘"</li>
          </ul>
        </div>
      </div>

      {/* Canva AI 소개 */}
      <h2 style={{ fontWeight: 'bold', marginBottom: '16px', fontSize: '1.5rem' }} id="canva-ai-소개">
        🎨 Canva AI 소개
      </h2>
      <p style={{ color: '#666', marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
        Canva AI는 전문적인 디자인 지식 없이도 텍스트만으로 아름다운 디자인을 생성할 수 있는 AI 기반 디자인 도구입니다. 
        수업 자료, 발표 자료, 웹페이지 등을 빠르고 쉽게 제작할 수 있습니다.
      </p>

      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600', 
          margin: 0,
          marginBottom: '16px',
          color: '#00C4CC'
        }}>
          주요 기능
        </h3>
        <ul style={{ 
          fontSize: '1rem', 
          color: '#666', 
          margin: 0,
          paddingLeft: '20px',
          lineHeight: '1.8'
        }}>
          <li><b>Magic Design</b>: 텍스트 입력만으로 전체 디자인 자동 생성</li>
          <li><b>Magic Write</b>: AI가 텍스트 내용을 작성하고 개선</li>
          <li><b>이미지 생성</b>: 텍스트로 이미지 자동 생성 (DALL·E, Midjourney 연동)</li>
          <li><b>배경 제거</b>: 한 번의 클릭으로 이미지 배경 제거</li>
          <li><b>디자인 추천</b>: 컨텍스트에 맞는 디자인 자동 제안</li>
          <li><b>웹사이트 생성</b>: 디자인을 기반으로 웹페이지 자동 생성</li>
        </ul>
        <div style={{ 
          display: 'flex', 
          gap: '12px',
          marginTop: '20px'
        }}>
          <a 
            href="https://www.canva.com" 
            target="_blank" 
            rel="noopener"
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              backgroundColor: '#00C4CC',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '1rem',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#00A0B0';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 196, 204, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#00C4CC';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Canva 열기 →
          </a>
        </div>
      </div>

      {/* Canva AI 활용하기 */}
      <h2 style={{ fontWeight: 'bold', marginBottom: '16px', fontSize: '1.5rem' }} id="canva-ai-활용">
        🚀 Canva AI 활용하기
      </h2>

      {/* 1. Canva AI로 PPT 만들기 */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600', 
          margin: 0,
          marginBottom: '12px',
          color: '#00C4CC'
        }}>
          1. Canva AI로 PPT 만들기
        </h3>
        <p style={{ 
          fontSize: '1rem', 
          color: '#666', 
          margin: 0,
          marginBottom: '16px',
          lineHeight: '1.6'
        }}>
          Canva AI에서 디자인 - 프레젠테이션을 선택하고, 만들고 싶은 내용을 첨부하거나 텍스트로 설명한 후에 고객, 스타일, 길이를 설정하면 PPT 개요가 생성됩니다. 개요를 확인하고 함께 다듬은 후 프레젠테이션을 선택하면 완성된 PPT를 받을 수 있습니다.
        </p>
        <div style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <h4 style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            margin: 0,
            marginBottom: '12px',
            color: '#333'
          }}>
            사용 방법
          </h4>
          <ol style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.8'
          }}>
            <li>Canva AI에서 "디자인" 메뉴에서 "프레젠테이션" 선택</li>
            <li>만들고 싶은 내용을 파일로 첨부하거나 텍스트로 설명 입력</li>
            <li>고객(대상), 스타일, 길이(슬라이드 수) 설정</li>
            <li>생성된 PPT 개요 확인 및 필요한 부분 수정</li>
            <li>개요를 함께 다듬고 완성</li>
            <li>원하는 프레젠테이션 스타일 선택하여 최종 PPT 생성</li>
            <li>생성된 슬라이드를 확인하고 내용 수정 및 추가</li>
          </ol>
        </div>
        <div style={{
          backgroundColor: '#e8f0fe',
          borderRadius: '8px',
          padding: '16px',
          borderLeft: '4px solid #00C4CC'
        }}>
          <h4 style={{ 
            fontSize: '0.875rem', 
            fontWeight: '600', 
            margin: 0,
            marginBottom: '8px',
            color: '#00C4CC'
          }}>
            💡 활용 팁
          </h4>
          <ul style={{ 
            fontSize: '0.875rem', 
            color: '#333', 
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.6'
          }}>
            <li>구체적인 주제와 목적을 명시하면 더 적합한 디자인이 생성됨</li>
            <li>생성된 디자인 중 하나를 선택한 후 추가 수정으로 완성도를 높일 수 있음</li>
            <li>Canva의 템플릿 라이브러리도 함께 활용하면 더 다양한 옵션 확보 가능</li>
            <li>PDF나 PowerPoint 형식으로 내보내기 가능</li>
          </ul>
        </div>
      </div>

      {/* 2. Canva AI로 웹페이지 생성하기 */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600', 
          margin: 0,
          marginBottom: '12px',
          color: '#00C4CC'
        }}>
          2. Canva AI로 웹페이지 생성하기
        </h3>
        <p style={{ 
          fontSize: '1rem', 
          color: '#666', 
          margin: 0,
          marginBottom: '16px',
          lineHeight: '1.6'
        }}>
          Canva의 웹사이트 생성 기능을 사용하면 디자인한 콘텐츠를 기반으로 실제 작동하는 웹페이지를 만들 수 있습니다. 
          수업 소개 페이지, 프로젝트 포트폴리오, 학습 자료 페이지 등을 쉽게 제작할 수 있습니다.
        </p>
        <div style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <h4 style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            margin: 0,
            marginBottom: '12px',
            color: '#333'
          }}>
            사용 방법
          </h4>
          <ol style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.8'
          }}>
            <li>Canva에서 "웹사이트" 템플릿 선택 또는 기존 디자인 사용</li>
            <li>Magic Design으로 웹페이지 레이아웃 자동 생성 (선택사항)</li>
            <li>원하는 내용과 이미지를 추가하여 디자인 구성</li>
            <li>"게시" 버튼 클릭하여 웹사이트로 변환</li>
            <li>생성된 웹페이지 URL 확인 및 공유</li>
            <li>필요시 도메인 연결 또는 HTML 다운로드 가능</li>
          </ol>
        </div>
        <div style={{
          backgroundColor: '#e8f0fe',
          borderRadius: '8px',
          padding: '16px',
          borderLeft: '4px solid #00C4CC'
        }}>
          <h4 style={{ 
            fontSize: '0.875rem', 
            fontWeight: '600', 
            margin: 0,
            marginBottom: '8px',
            color: '#00C4CC'
          }}>
            💡 활용 예시
          </h4>
          <ul style={{ 
            fontSize: '0.875rem', 
            color: '#333', 
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.6'
          }}>
            <li>학급 소개 웹페이지: 반 학생들과 수업 활동 사진을 포함한 페이지</li>
            <li>프로젝트 포트폴리오: 학생 작품을 전시하는 웹 갤러리</li>
            <li>수업 자료 페이지: 학습 자료와 과제를 안내하는 페이지</li>
            <li>이벤트 안내 페이지: 체험학습, 수학여행 등 이벤트 소개 페이지</li>
          </ul>
        </div>
      </div>

      {/* 실습 과제 */}
      <h2 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.5rem' }} id="실습-과제">
        실습 과제
      </h2>
      <p style={{ color: '#666', marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
        연수 중 진행할 실습 과제입니다. 아래 항목 중 하나 이상을 선택하여 실습해보세요.
      </p>
      <div style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <h3 style={{ 
          fontSize: '1.125rem', 
          fontWeight: '600', 
          margin: 0,
          marginBottom: '16px',
          color: '#333'
        }}>
          Gemini Pro 실습 과제
        </h3>
        <ul style={{ 
          fontSize: '1rem', 
          color: '#666', 
          margin: 0,
          paddingLeft: '20px',
          lineHeight: '1.8'
        }}>
          <li style={{ marginBottom: '12px' }}>
            <b>과제 1: Workspace 연동 활용</b><br/>
            Gemini Pro를 Workspace와 연결하고, 이메일 요약, 일정 관리, 드라이브 파일 분석 중 하나를 실습해보세요.
          </li>
          <li style={{ marginBottom: '12px' }}>
            <b>과제 2: Nanobanana Pro 이미지 편집</b><br/>
            자신의 사진을 업로드하고 원하는 스타일로 변경해보세요. (헤어스타일, 복장 변경 등)
          </li>
          <li style={{ marginBottom: '12px' }}>
            <b>과제 3: Canvas로 수업 자료 만들기</b><br/>
            교과서 PDF를 업로드하여 Google Slides 수업 자료를 생성하거나, 바이브코딩으로 간단한 웹 도구를 만들어보세요.
          </li>
          <li style={{ marginBottom: '12px' }}>
            <b>과제 4: Google Flow 영상 제작</b><br/>
            수업 소개나 학습 콘텐츠 영상을 만들어보세요.
          </li>
        </ul>

        <h3 style={{ 
          fontSize: '1.125rem', 
          fontWeight: '600', 
          margin: 0,
          marginTop: '24px',
          marginBottom: '16px',
          color: '#333'
        }}>
          Canva AI 실습 과제
        </h3>
        <ul style={{ 
          fontSize: '1rem', 
          color: '#666', 
          margin: 0,
          paddingLeft: '20px',
          lineHeight: '1.8'
        }}>
          <li style={{ marginBottom: '12px' }}>
            <b>과제 1: Magic Design으로 PPT 만들기</b><br/>
            자신의 교과 주제로 Magic Design 기능을 사용하여 수업 자료 PPT를 만들어보세요.
          </li>
          <li style={{ marginBottom: '12px' }}>
            <b>과제 2: 웹페이지 생성하기</b><br/>
            Canva로 디자인한 콘텐츠를 웹페이지로 변환하여 공유 가능한 페이지를 만들어보세요.
          </li>
        </ul>
      </div>

      {/* 참고 자료 */}
      <h2 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.5rem' }} id="참고-자료">
        참고 자료
      </h2>
      <div style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <ul style={{ 
          fontSize: '1rem', 
          color: '#666', 
          margin: 0,
          paddingLeft: '20px',
          lineHeight: '1.8'
        }}>
          <li>
            <b>Gemini Pro 관련</b>
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li><a href="https://gemini.google.com" target="_blank" rel="noopener" style={{color: '#1976d2'}}>Gemini 공식 사이트</a></li>
              <li><a href="https://aistudio.google.com/" target="_blank" rel="noopener" style={{color: '#1976d2'}}>Google AI Studio</a></li>
              <li><a href="https://workspace.google.com/" target="_blank" rel="noopener" style={{color: '#1976d2'}}>Google Workspace</a></li>
            </ul>
          </li>
          <li style={{ marginTop: '16px' }}>
            <b>Canva AI 관련</b>
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li><a href="https://www.canva.com" target="_blank" rel="noopener" style={{color: '#1976d2'}}>Canva 공식 사이트</a></li>
              <li><a href="https://www.canva.com/designschool/" target="_blank" rel="noopener" style={{color: '#1976d2'}}>Canva Design School</a></li>
            </ul>
          </li>
          <li style={{ marginTop: '16px' }}>
            <b>교육용 AI 활용 가이드</b>
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li>Google for Education 리소스</li>
              <li>교육부 AI 디지털교과서 플랫폼</li>
            </ul>
          </li>
        </ul>
      </div>

      {/* 연수 문의 */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '24px',
        marginBottom: '32px'
      }}>
        <h3 style={{ 
          fontSize: '1.125rem', 
          fontWeight: '600', 
          margin: 0,
          marginBottom: '12px',
          color: '#333'
        }}>
          연수 문의
        </h3>
        <p style={{ 
          fontSize: '1rem', 
          color: '#666', 
          margin: 0,
          lineHeight: '1.6'
        }}>
          추가 문의사항이나 도움이 필요하신 경우 연수 담당자에게 연락해주세요.<br/>
          이메일: jihotech16@outlook.kr
        </p>
      </div>
    </div>
  );
};

export default SunrinAITraining;
